import config from 'common/oldJavascripts/config.js';
import React, { Component } from 'react';
import LayoutContent from 'common/oldJavascripts/components/Base/Layout/LayoutContent';
import Box from 'common/oldJavascripts/components/Base/Box';
import Button from 'common/oldJavascripts/components/Base/Button';
import Header from 'common/oldJavascripts/components/Base/Header';

import Pagination from 'common/oldJavascripts/components/Base/Pagination';
import Blankslate from 'common/oldJavascripts/components/Base/Blankslate';
import Loader from 'common/components/Loader';
import Search from 'common/oldJavascripts/components/Shared/Search';
import UserRow from './Crew/UserRow';
import FailureAlert from 'common/oldJavascripts/components/Shared/FailureAlert';
import ajax from 'common/utilities/ajax';
import Sentry from '@sentry/browser';
import QueryRefresher from 'common/components/QueryRefresher';

// HoC
import withApi from 'common/hoc/withApi';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withStyles } from '@material-ui/core/styles';
import withPermissionProtection from 'common/hoc/withPermissionProtection';

// Actions
import {
  pushNotification,
  popNotification,
} from 'common/store/actions/snackbarNotifications';
import * as SnackbarVariants from 'common/constants/componentData/snackbarVariants';

import BoxItem from 'common/oldJavascripts/components/Base/Box/BoxItem';

import ExpandableTable from 'common/oldJavascripts/components/Base/ExpandableTable';
import ExpandableTableRow from 'common/oldJavascripts/components/Base/ExpandableTable/ExpandableTableRow';
import ExpandableTableCell from 'common/oldJavascripts/components/Base/ExpandableTable/ExpandableTableCell';

const ETableRow = ExpandableTableRow;
const ETableCell = ExpandableTableCell;

const styles = () => ({
  button: {
    margin: '5px',
  },
  loader: {
    backgroundColor: 'white',
    width: '100%',
    height: '200px',
  },
});

class Crew extends Component {
  static queries = {
    crew: {
      info(params, related) {
        const routerParams = related['/router/params'];
        const query = related['/router/query'];

        return {
          id: `/projects/${routerParams.projectId}/crew`,

          cursor: {
            page: query.page,
            per_page: query.per_page || config.DEFAULT_PER_PAGE,
            sort: query.sort,
            direction: query.direction,
            q: query.q,
          },
        };
      },
    },

    project: {
      info(_, related) {
        const routerParams = related['/router/params'];

        return {
          id: `/projects/${routerParams.projectId}`,
        };
      },
    },

    me: {
      info() {
        return { id: '/me' };
      },
    },
  };

  render() {
    return (
      <LayoutContent extraPadding={true}>
        <Box>
          {this._renderHeader()}
          {this._renderContent()}
        </Box>
        {this._renderPagination()}
      </LayoutContent>
    );
  }

  _renderHeader() {
    const { crew: { reload: reloadQuery } = {} } = this.props;
    return (
      <BoxItem>
        <Header>
          <Header.Title>Crew</Header.Title>
          <QueryRefresher reloadQuery={reloadQuery} />
          <Header.Nav>
            <Search />
            {this._renderHeaderNav()}
          </Header.Nav>
        </Header>
      </BoxItem>
    );
  }

  exportCrew = () => {
    const {
      routerParams: { projectId } = {},
      pushNotification,
      popNotification,
    } = this.props;
    pushNotification({
      message: 'Requesting the crew list for download.',
      variant: SnackbarVariants.INFO,
    });
    ajax
      .get(`/v2/projects/${projectId}/export-crew`)
      .then(({ path }) => {
        popNotification();
        pushNotification({
          message: 'Downloading crew list now.',
          variant: SnackbarVariants.SUCCESS,
        });
        if (path) {
          window.location.href = path;
        } else {
          throw new Error(
            `No path received for export crew for project: ${projectId}`,
          );
        }
      })
      .catch(e => {
        popNotification();
        pushNotification({
          message: `There was an issue downloading the crew list.
            Please try again or contact customer support if the issue persists.`,
          variant: SnackbarVariants.ERROR,
        });
        Sentry.captureException(e);
      });
  };

  exportEmergency = () => {
    const {
      routerParams: { projectId } = {},
      pushNotification,
      popNotification,
    } = this.props;
    pushNotification({
      message: 'Requesting the emergency contact list for download.',
      variant: SnackbarVariants.INFO,
    });
    ajax
      .get(`/v2/projects/${projectId}/export-crew-emergency-contacts`)
      .then(({ path }) => {
        popNotification();
        pushNotification({
          message: 'Downloading emergency contacts list now.',
          variant: SnackbarVariants.SUCCESS,
        });
        if (path) {
          window.location.href = path;
        } else {
          throw new Error(
            `No path received for export emergency contacts for project: ${projectId}`,
          );
        }
      })
      .catch(e => {
        popNotification();
        pushNotification({
          message: `There was an issue downloading the emergency contacts list.
            Please try again or contact customer support if the issue persists.`,
          variant: SnackbarVariants.ERROR,
        });
        Sentry.captureException(e);
      });
  };

  _renderHeaderNav = () => {
    const { classes = {}, me: { data: meData = {} } = {} } = this.props;
    const { is_support_admin } = meData || {};

    if (!is_support_admin) {
      return (
        <React.Fragment>
          <Button className={classes.button} onClick={this.exportCrew}>
            Export Crew List
          </Button>
          <Button className={classes.button} onClick={this.exportEmergency}>
            Export Emergency Contacts
          </Button>
          <Button className={classes.button} to="crew/new">
            Add crew member
          </Button>
          <Button className={classes.button} to="crew/import">
            Bulk upload crew
          </Button>
        </React.Fragment>
      );
    }
  };

  _renderContent = () => {
    const {
      crew: { status, data: { items: crewMembers = [] } = {} } = {},
      classes = {},
    } = this.props;

    if (status === 'loading') {
      return <Loader className={classes.loader} />;
    }

    if (status === 'failed') {
      return <FailureAlert />;
    }

    if (crewMembers.length > 0) {
      return this._renderCrewMembers();
    }

    return <Blankslate>No crew members</Blankslate>;
  };

  _renderCrewMembers = () => {
    const { crew: crewMutator = {} } = this.props;
    const { data: crew = {} } = crewMutator || {};
    return (
      <ExpandableTable>
        <ETableRow>
          <ETableCell header={true} sort="name">
            Employee
          </ETableCell>
          <ETableCell header={true} sort="email">
            Email
          </ETableCell>
          <ETableCell header={true} sort="has_account">
            Has Account
          </ETableCell>
          <ETableCell header={true} sort="offers">
            Offers
          </ETableCell>
          <ETableCell header={true} />
        </ETableRow>
        {crew.items.map(user => (
          <UserRow
            params={{ id: user.id }}
            key={user.id}
            crewListReload={crewMutator.reload}
          />
        ))}
      </ExpandableTable>
    );
  };

  _renderPagination = () => {
    const { crew: { data: crew = {} } = {} } = this.props;
    const { total_pages, page } = crew || {};
    if (total_pages > 0) {
      return <Pagination totalPages={total_pages} page={page} />;
    }
    return null;
  };
}

const mapStateToProps = () => ({});
const mapDispatchToProps = {
  pushNotification,
  popNotification,
};

export default compose(
  withPermissionProtection('can_create_offers'),
  withApi,
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withStyles(styles),
)(Crew);
